import React, { useState, useEffect, createContext, useContext } from 'react'

import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import { getFullLocation } from '../../helpers'
import { listingRegex } from '../../constants/regex'

// Contexts
import { AppContext } from '../AppContext'

//Constants
import {
  ZILLOW_DOM_STATUS_ID,
  DENY_LISTING_STATUSES,
  ZILLOW_SUMMARY_SELECTOR,
  ZILLOW_SUMMARY_SELECTOR_2,
  FETCH_ZILLOW_ELEMENT_TIMEOUT_MS,
  ZILLOW_OWNED_SELECTOR,
} from '../../constants/zillow'

export const ZillowContext = createContext({
  locationUrl: null,
  proxiedZillowUrl: null,
})

const parseAllowedAndStatus = () => {
  let isAllowed, status

  // Order dependent, the top selector is the least specific
  const listingStatusContainer =
    document.querySelector(ZILLOW_SUMMARY_SELECTOR) ||
    document.querySelector(ZILLOW_SUMMARY_SELECTOR_2) ||
    document.querySelector(ZILLOW_DOM_STATUS_ID) ||
    document.querySelector(ZILLOW_OWNED_SELECTOR)

  if (listingStatusContainer) {
    const listingStatusText = listingStatusContainer.textContent.toLowerCase()

    const isADeniedStatus = DENY_LISTING_STATUSES.some((status) =>
      listingStatusText.includes(status)
    )

    isAllowed = !isADeniedStatus
    status = listingStatusText
  } else {
    isAllowed = false
    status = null
  }

  return {
    isAllowed,
    status,
  }
}

export const ZillowContextProvider = ({ zillowUrl, children }) => {
  const { setIncludeRental } = useContext(AppContext)
  const [locationUrl, setLocationUrl] = useState(zillowUrl || getFullLocation())
  const [hasAllowedStatus, setHasAllowedStatus] = useState(false)
  const [address, setAddress] = useState(null)
  const [imageUrl, setImageUrl] = useState(null)
  const [listingPrice, setListingPrice] = useState(null)
  const [listingStatus, setListingStatus] = useState(null)
  const [userManuallyEntered, setUserManuallyEntered] = useState(false)
  const [isAddressError, setIsAddressError] = useState(false)
  const [isListingPriceError, setIsListingPriceError] = useState(false)
  const [isStatusError, setIsStatusError] = useState(false)

  const parseAddressFromDom = () => {
    let parsedAddress = ''
    let parsedUrl = ''
    let parsedListingPrice = ''

    let addressNode = window.document.querySelector('#ds-chip-property-address')
    let addressString = addressNode?.textContent

    if (!addressString) {
      addressNode = window.document.querySelector('.summary-container h1')
      addressString = addressNode?.textContent
    }

    // Handle Lazy Loading of Zillow Address Scenario
    if (addressString && !addressString.includes('--')) {
      // Overwrite Address
      parsedAddress = addressString
    }

    // Fetch Price
    parsedListingPrice = window.document
      .querySelector('.ds-summary-row > span')
      ?.textContent?.replace(/[$,]+/g, '')

    if (!parsedListingPrice) {
      parsedListingPrice = window.document
        .querySelector("[data-testid='price']")
        ?.textContent?.replace(/[$,]+/g, '')
    }

    // Fetch Image
    parsedUrl = window.document.querySelector(
      '.media-stream-tile--prominent img'
    )?.src

    return {
      parsedAddress,
      parsedUrl,
      parsedListingPrice,
    }
  }

  useEffect(() => {
    const statusObj = parseAllowedAndStatus()
    setHasAllowedStatus(statusObj.isAllowed)
    setListingStatus(statusObj.status)

    const observer = new MutationObserver((mutations) => {
      if (userManuallyEntered) return

      mutations.forEach((_m) => {
        const {
          parsedAddress,
          parsedUrl,
          parsedListingPrice,
        } = parseAddressFromDom()

        const newUrl = zillowUrl || getFullLocation()

        if (/zillow/.test(newUrl)) {
          // Guard for Storybook
          const statusObj = parseAllowedAndStatus()
          setHasAllowedStatus(statusObj.isAllowed)
          setListingStatus(statusObj.status)

          // Url Has Changed
          if (locationUrl !== newUrl) {
            setLocationUrl(newUrl)
            setIncludeRental(false)
            setAddress(null)
            setListingPrice(null)
          }
        }

        if (!isEmpty(parsedAddress) && parsedAddress !== address) {
          setAddress(parsedAddress)
        }

        if (!isEmpty(parsedListingPrice)) {
          const floatPrice = parseFloat(parsedListingPrice, 10)

          floatPrice && setListingPrice(floatPrice)
        }

        setImageUrl(parsedUrl)
      })
    })

    const body = document.querySelector('body')
    const config = {
      childList: true,
      subtree: true,
      characterData: true,
    }

    observer.observe(body, config)

    return () => {
      observer.disconnect()
    }
  }, [
    locationUrl,
    zillowUrl,
    setIncludeRental,
    setHasAllowedStatus,
    address,
    userManuallyEntered,
  ])

  useEffect(() => {
    const timeOut = () => {
      // Runs after 15 seconds
      const currentUrl = getFullLocation()
      const unfetchedAttributes = []

      // If not on a Zillow listing url - exit
      if (!listingRegex.test(currentUrl)) return

      // If the listing status was never found - fire error to sentry
      if (listingStatus === null) {
        unfetchedAttributes.push('status')
        setIsStatusError(true)

        window.Sentry.captureException(
          new Error(
            `Could not find the following Zillow DOM elements in ${
              FETCH_ZILLOW_ELEMENT_TIMEOUT_MS / 1000
            }s: ${unfetchedAttributes.join(', ')}`
          )
        )

        return
      }

      // If address or listing price wasn't found
      // And it is a valid listing type (ie For Sale)
      // Fire Error to Sentry
      if (address == null) {
        unfetchedAttributes.push('address')
        setIsAddressError(true)
      }

      if (listingPrice == null) {
        unfetchedAttributes.push('listingPrice')
        setIsListingPriceError(true)
      }

      // Homes owned by Zillow has an entirely different dom structure
      // So we want the error modal to pop open but we don't want to send to Sentrys
      const isOwnedByZillowHome = document.querySelector(ZILLOW_OWNED_SELECTOR)

      if (unfetchedAttributes.length && hasAllowedStatus && !isOwnedByZillowHome) {
        window.Sentry.captureException(
          new Error(
            `Could not find the following Zillow DOM elements in ${
              FETCH_ZILLOW_ELEMENT_TIMEOUT_MS / 1000
            }s: ${unfetchedAttributes.join(', ')}`
          )
        )
      }
    }
    const timer = setTimeout(timeOut, FETCH_ZILLOW_ELEMENT_TIMEOUT_MS)

    return () => clearTimeout(timer)
  }, [locationUrl, address, listingPrice, listingStatus, hasAllowedStatus])

  const match = locationUrl.match(listingRegex)
  const zpid = match?.groups?.id

  // TODO: Replace with our own CORS proxy
  const proxiedZillowUrl = `http://0.0.0.0:1000/${zillowUrl}`

  const resetAndFetchManualAddress = (address, listingPrice) => {
    setAddress(address)
    setListingPrice(listingPrice)
    setIsListingPriceError(false)
    setIsAddressError(false)
    setUserManuallyEntered(true)
    setIsStatusError(true)
  }

  const value = {
    locationUrl,
    proxiedZillowUrl,
    showBothead: Boolean(zpid) && hasAllowedStatus,
    address,
    setAddress,
    isAddressError,
    imageUrl,
    listingPrice,
    setListingPrice,
    isListingPriceError,
    isStatusError,
    userManuallyEntered,
    setUserManuallyEntered,
    setIsAddressError,
    setIsListingPriceError,
    resetAndFetchManualAddress,
  }

  // Guard Clause - wait till Children are ready
  return (
    <ZillowContext.Provider value={value}>{children}</ZillowContext.Provider>
  )
}

ZillowContextProvider.defaultProps = {
  devMode: false,
  zillowUrl: null,
}

ZillowContextProvider.propTypes = {
  devMode: PropTypes.bool,
  zillowUrl: PropTypes.string,
  children: PropTypes.any,
}
